import { Board, Group } from "./equalGroups.ts";

/*
 * The rules of Equal Groups as pure functions (ADR 0127 §1, §3): select up to
 * four tiles, Check them, find a group or spend one of four mistakes. A game is
 * its board plus the guesses made, so a reload replays the guesses over the same
 * board (`replay`) and nothing else needs saving.
 */

export const MAX_MISTAKES = 4;
export const GROUP_SIZE = 4;

export interface Guess {
    /** The four tile ids, sorted: the same four tiles are the same guess in any order. */
    ids: string[];
    correct: boolean;
}

export interface GameState {
    board: Board;
    /** Selected tile ids, in the order picked. */
    selected: string[];
    /** Group indexes, in the order found. */
    found: number[];
    guesses: Guess[];
    mistakes: number;
    status: "playing" | "won" | "lost";
}

export type CheckResult =
    | { kind: "ignored" }
    | { kind: "repeat" }
    | { kind: "found"; groupIndex: number; value: number }
    /** `maxEqual`: the most of the four chosen tiles that share a value (1, 2 or 3). */
    | { kind: "wrong"; maxEqual: number; mistakesLeft: number; gameOver: boolean };

export function newGame(board: Board): GameState {
    return { board, selected: [], found: [], guesses: [], mistakes: 0, status: "playing" };
}

function tileGroups(board: Board): Map<string, number> {
    const map = new Map<string, number>();
    board.groups.forEach((group, index) => group.tiles.forEach((tile) => map.set(tile.id, index)));
    return map;
}

/** The tile ids still on the grid (not in a found group). */
export function remainingIds(state: GameState): string[] {
    const groups = tileGroups(state.board);
    return state.board.order.filter((id) => !state.found.includes(groups.get(id)!));
}

export function toggleTile(state: GameState, id: string): GameState {
    if (state.status !== "playing" || !remainingIds(state).includes(id)) {
        return state;
    }
    if (state.selected.includes(id)) {
        return { ...state, selected: state.selected.filter((picked) => picked !== id) };
    }
    if (state.selected.length >= GROUP_SIZE) {
        return state;
    }
    return { ...state, selected: [...state.selected, id] };
}

export function clearSelection(state: GameState): GameState {
    return state.selected.length === 0 ? state : { ...state, selected: [] };
}

const keyOf = (ids: string[]): string => [...ids].sort().join(",");

export function check(state: GameState): { state: GameState; result: CheckResult } {
    if (state.status !== "playing" || state.selected.length !== GROUP_SIZE) {
        return { state, result: { kind: "ignored" } };
    }
    const ids = [...state.selected].sort();
    // The same four tried again is not a new mistake (and not a new guess in the result).
    if (state.guesses.some((guess) => !guess.correct && keyOf(guess.ids) === keyOf(ids))) {
        return { state, result: { kind: "repeat" } };
    }

    const groups = tileGroups(state.board);
    const counts = new Map<number, number>();
    ids.forEach((id) => counts.set(groups.get(id)!, (counts.get(groups.get(id)!) ?? 0) + 1));
    const [bestGroup, maxEqual] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];

    if (maxEqual === GROUP_SIZE) {
        const found = [...state.found, bestGroup];
        const status = found.length === state.board.groups.length ? "won" : "playing";
        return {
            state: {
                ...state,
                selected: [],
                found,
                guesses: [...state.guesses, { ids, correct: true }],
                status,
            },
            result: { kind: "found", groupIndex: bestGroup, value: state.board.groups[bestGroup].value },
        };
    }

    const mistakes = state.mistakes + 1;
    const gameOver = mistakes >= MAX_MISTAKES;
    return {
        state: {
            ...state,
            selected: gameOver ? [] : state.selected,
            guesses: [...state.guesses, { ids, correct: false }],
            mistakes,
            status: gameOver ? "lost" : "playing",
        },
        result: { kind: "wrong", maxEqual, mistakesLeft: MAX_MISTAKES - mistakes, gameOver },
    };
}

/** The game as it stood after these guesses (each a list of four tile ids), for a reload. */
export function replay(board: Board, guesses: string[][]): GameState {
    let state = newGame(board);
    for (const ids of guesses) {
        state = ids.reduce((next, id) => toggleTile(next, id), clearSelection(state));
        state = check(state).state;
    }
    return clearSelection(state);
}

/** The guesses as saved: just the tile ids, in order. */
export function guessIds(state: GameState): string[][] {
    return state.guesses.map((guess) => guess.ids);
}

/** Groups not found yet, lowest value first, for the reveal when the game ends. */
export function unfoundGroups(state: GameState): Group[] {
    return state.board.groups.filter((_, index) => !state.found.includes(index));
}

/** One ✓ or ✗ for each guess, in order. */
export function marks(state: GameState): string {
    return state.guesses.map((guess) => (guess.correct ? "✓" : "✗")).join("");
}

/** The text Copy result puts on the clipboard: no tile is in it, so it gives nothing away. */
export function shareText(heading: string, state: GameState, url: string): string {
    return `${heading}\n${marks(state)}\n${url}`;
}
